import authClient from "src/lib/auth/authClient";
import { store } from "src/lib/store/store";
import { sessionExpired, setTokens } from "src/lib/features/auth/authSlice";
import type { SessionExpiredReason } from "src/lib/features/auth/auth.type";
import {
  ACCESS_TOKEN_EXPIRY,
  REFRESH_TOKEN,
  TOKEN,
} from "src/utils/constants";

/**
 * Access-token refresh, with the three properties that make rotation safe:
 *
 *   single-flight  one refresh per burst of 401s, everyone else queues
 *   cross-tab      one tab leads, the others adopt its result
 *   fail-closed    anything unexpected ends the session rather than looping
 *
 * Rotation means each refresh retires its predecessor, and replaying a
 * retired token is treated by the backend as theft (the whole token family
 * gets revoked). So a burst of parallel refreshes is not merely wasteful --
 * it would log the user out. Hence the care below.
 *
 * Ported from clientApp; the talent app's storage keys differ, nothing else.
 */

/* Backend error codes -> what we tell the user. */
const REASON_BY_CODE: Record<string, SessionExpiredReason> = {
  REFRESH_EXPIRED: "idle",
  REFRESH_CAP: "cap",
  REFRESH_REUSED: "revoked",
  REFRESH_INVALID: "unknown",
};

type RefreshResult = { accessToken: string } | null;

/* The in-flight refresh, shared by every caller that arrives while it runs. */
let inFlight: Promise<RefreshResult> | null = null;

/* Cross-tab coordination. Tabs that see a peer's fresh tokens adopt them
   instead of running their own refresh, which would replay a retired token.
   The backend's 10s grace window covers the residual race (a tab that
   crashes mid-refresh, or a browser without BroadcastChannel). The channel
   name is per-app: a BroadcastChannel is scoped to the origin, but naming it
   after the audience keeps a stray message from another app from ever
   being mistaken for this one's tokens. */
const CHANNEL_NAME = "dw-auth-talent";
const channel: BroadcastChannel | null =
  typeof BroadcastChannel !== "undefined"
    ? new BroadcastChannel(CHANNEL_NAME)
    : null;

type PeerMessage =
  | { type: "refreshed"; accessToken: string; refreshToken: string; expiresAt: number }
  | { type: "expired"; reason: SessionExpiredReason };

if (channel) {
  channel.onmessage = (event: MessageEvent<PeerMessage>) => {
    const message = event.data;
    if (!message) return;

    if (message.type === "refreshed") {
      writeTokens(message.accessToken, message.refreshToken, message.expiresAt);
      store.dispatch(
        setTokens({
          accessToken: message.accessToken,
          refreshToken: message.refreshToken,
        })
      );
    }

    if (message.type === "expired") {
      clearTokens();
      store.dispatch(sessionExpired(message.reason));
    }
  };
}

const readRefreshToken = () => {
  try {
    return localStorage.getItem(REFRESH_TOKEN);
  } catch {
    /* Private mode / blocked storage. Treat as no session. */
    return null;
  }
};

const writeTokens = (
  accessToken: string,
  refreshToken: string,
  expiresAt: number
) => {
  try {
    localStorage.setItem(TOKEN, accessToken);
    localStorage.setItem(REFRESH_TOKEN, refreshToken);
    localStorage.setItem(ACCESS_TOKEN_EXPIRY, String(expiresAt));
  } catch {
    /* Non-fatal: the tokens still live in redux for this page's lifetime. */
  }
};

/**
 * Persist a freshly issued pair (from /auth/exchange or a peer tab) into
 * storage and redux, and let the other tabs know. `expiresIn` is seconds,
 * as the API sends it; defaults to the contract's 15 minutes if absent.
 */
export const persistTokenPair = (pair: {
  accessToken: string;
  refreshToken: string;
  expiresIn?: number;
}) => {
  const expiresIn = Number(pair.expiresIn);
  const expiresAt =
    Date.now() + (Number.isFinite(expiresIn) ? expiresIn : 900) * 1000;

  writeTokens(pair.accessToken, pair.refreshToken, expiresAt);
  store.dispatch(
    setTokens({ accessToken: pair.accessToken, refreshToken: pair.refreshToken })
  );
  channel?.postMessage({
    type: "refreshed",
    accessToken: pair.accessToken,
    refreshToken: pair.refreshToken,
    expiresAt,
  } satisfies PeerMessage);
};

/**
 * Cold-load hydration: put whatever pair is in storage into redux so the
 * request interceptor can attach it. Without this, every page load would
 * start with an empty store, 401, and burn a refresh (and a rotation) for
 * nothing. Returns whether a refresh token was found.
 */
export const hydrateFromStorage = (): boolean => {
  const refreshToken = readRefreshToken();
  if (!refreshToken) return false;

  let accessToken = "";
  try {
    accessToken = localStorage.getItem(TOKEN) ?? "";
  } catch {
    /* fall through with an empty access token; the interceptor will refresh */
  }

  store.dispatch(setTokens({ accessToken, refreshToken }));
  return true;
};

const clearTokens = () => {
  try {
    localStorage.removeItem(TOKEN);
    localStorage.removeItem(REFRESH_TOKEN);
    localStorage.removeItem(ACCESS_TOKEN_EXPIRY);
  } catch {
    /* nothing to clean up */
  }
};

/** End the session: wipe credentials, tell the other tabs, raise the modal. */
export const endSession = (reason: SessionExpiredReason) => {
  clearTokens();
  store.dispatch(sessionExpired(reason));
  channel?.postMessage({ type: "expired", reason } satisfies PeerMessage);
};

const performRefresh = async (): Promise<RefreshResult> => {
  const refreshToken = readRefreshToken();

  /* No refresh token: a pre-migration session started via the legacy
     ?token= handoff. There is nothing to refresh, so the 401 that got us
     here is terminal. */
  if (!refreshToken) {
    endSession("unknown");
    return null;
  }

  try {
    const { data } = await authClient.post("/auth/refresh", { refreshToken });

    const accessToken = data?.data?.accessToken;
    const nextRefreshToken = data?.data?.refreshToken;

    /* A 200 that does not carry a usable pair is a contract violation. Fail
       closed rather than retrying into a loop. */
    if (!accessToken || !nextRefreshToken) {
      endSession("unknown");
      return null;
    }

    persistTokenPair({
      accessToken,
      refreshToken: nextRefreshToken,
      expiresIn: data?.data?.expiresIn,
    });

    return { accessToken };
  } catch (error: any) {
    /* No response means the network dropped, not that the session died.
       Surfacing the failure lets the original request reject normally and
       leaves the user logged in. */
    if (!error?.response) {
      return null;
    }

    /* Only a 401 means the refresh token is gone. Anything else -- the
       429 from the rate limiter, a 5xx mid-deploy -- is a failed attempt
       against a session that is still valid; the original request rejects
       and the user stays logged in. */
    if (error.response.status !== 401) {
      return null;
    }

    const code = error.response?.data?.error?.code as string | undefined;
    endSession((code && REASON_BY_CODE[code]) || "unknown");
    return null;
  }
};

/**
 * Get a fresh access token, coalescing concurrent callers.
 *
 * Returns the new token, or null if the session is over (in which case the
 * modal has already been raised) or the network failed.
 */
export const refreshAccessToken = (): Promise<RefreshResult> => {
  if (inFlight) return inFlight;

  /* Single-flight covers concurrency *within* this tab. Across tabs, two can
     still refresh simultaneously -- the broadcast only helps a tab that has
     not started yet. That residual race is what the backend's 10s grace
     window absorbs: the second tab replays a just-retired token and gets the
     same successor pair back instead of tripping reuse detection. */
  inFlight = performRefresh().finally(() => {
    inFlight = null;
  });

  return inFlight;
};

/** Test seam: drop any in-flight state between cases. */
export const __resetRefreshManager = () => {
  inFlight = null;
};
